import { TableRow, TableHead, TableHeader } from '@src/design-system/table';
import { flexRender } from '@tanstack/react-table';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@src/design-system/helpers';
import { useTiclickTableContext } from './context/ticlick-table-context-hook';
import { getPinningStyles } from './styles';
import { useEffect, useRef } from 'react';
import viewApi from '@shared/features/views/services/view-api';
import { ColumnHeaderFilterTrigger } from '@src/shared/features/views/components/static-filter-inputs';
import { useGetColumnName } from '@src/shared/hooks/use-get-column-name';
import { useTableStates } from './hooks/use-table-states';

export default function TiclickTableHeader<T>() {
  const { table } = useTiclickTableContext<T>();

  const getColumnName = useGetColumnName();

  return (
    <TableHeader>
      {table?.getHeaderGroups().map((headerGroup) => (
        <TableRow
          key={headerGroup.id}
          style={{ position: 'sticky', top: 0, zIndex: 20 }}
        >
          {headerGroup.headers.map((header) => {
            const { column } = header;
            const isPinned = column.getIsPinned();
            const isLastLeftPinned =
              isPinned === 'left' && column.getIsLastColumn('left');
            const isFirstRightPinned =
              isPinned === 'right' && column.getIsFirstColumn('right');

            const { displayed } = getColumnName(column);

            return (
              <TableHead
                key={header.id}
                style={{
                  ...getPinningStyles(column),
                }}
                data-pinned={isPinned || undefined}
                data-last-col={
                  isLastLeftPinned
                    ? 'left'
                    : isFirstRightPinned
                      ? 'right'
                      : undefined
                }
                className={cn(
                  'group h-11 bg-primary-50  border-r border-pale-border data-pinned:bg-primary-100/80 relative truncate data-pinned:backdrop-blur-xs [&:not([data-pinned]):has(+[data-pinned])_div.cursor-col-resize:last-child]:opacity-0 [&[data-last-col=left]_div.cursor-col-resize:last-child]:opacity-0 [&[data-pinned=right]]:border-r [&[data-pinned=right]:last-child_div.cursor-col-resize:last-child]:opacity-0 [&[data-pinned=right][data-last-col=right]]:border-l'
                )}
              >
                {header.isPlaceholder ? null : header.column.getCanSort() ? (
                  <div className='flex h-full w-full min-w-0 items-center gap-1'>
                    <div
                      className={cn(
                        'flex min-w-0 flex-1 cursor-pointer items-center gap-2 select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                      onKeyDown={(e) => {
                        if (
                          header.column.getCanSort() &&
                          (e.key === 'Enter' || e.key === ' ')
                        ) {
                          e.preventDefault();
                          header.column.getToggleSortingHandler()?.(e);
                        }
                      }}
                      tabIndex={header.column.getCanSort() ? 0 : undefined}
                    >
                      <span
                        ref={(node) => {
                          if (!node) return;
                          node.title = node.textContent || '';
                        }}
                        className='min-w-0 flex-1 truncate'
                      >
                        {flexRender(displayed, header.getContext())}
                      </span>

                      <ColumnHeaderFilterTrigger column={header.column} />

                      {{
                        asc: (
                          <ChevronUpIcon
                            className='shrink-0 opacity-60'
                            size={16}
                          />
                        ),
                        desc: (
                          <ChevronDownIcon
                            className='shrink-0 opacity-60'
                            size={16}
                          />
                        ),
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  </div>
                ) : (
                  <div className='flex w-full min-w-0 items-center gap-1'>
                    <span
                      ref={(node) => {
                        if (!node) return;
                        node.title = node.textContent || '';
                      }}
                      className='min-w-0 flex-1 truncate'
                    >
                      {flexRender(displayed, header.getContext())}
                    </span>

                    <ColumnHeaderFilterTrigger column={header.column} />
                  </div>
                )}
                {header.column.getIsResizing() && <ResizeHandle />}

                {header.column.getCanResize() && (
                  <div
                    onMouseDown={header.getResizeHandler()}
                    onTouchStart={header.getResizeHandler()}
                    className=' absolute right-0 inset-y-0 w-[4px] bg-transparent cursor-ew-resize transition hover:bg-primary-600'
                  />
                )}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </TableHeader>
  );
}

const ResizeHandle = () => {
  const { mutate: editView } = viewApi.useUpdate();
  const { columnSizing, currentView } = useTableStates();

  // Use ref to capture the latest columnSizing values without triggering re-renders
  const columnSizingRef = useRef(columnSizing);
  columnSizingRef.current = columnSizing;

  // Save column width changes when resize ends by using useEffect cleanup function
  // This approach ensures the API call only happens when the component unmounts,
  // avoiding excessive API calls during the resize process
  useEffect(() => {
    return () => {
      if (!currentView?.id) return;
      editView({
        id: currentView?.id,
        visible_static_fields: {
          ...(currentView?.visible_static_fields || {}),
          sizing: columnSizingRef.current,
        },
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className='absolute right-0 top-0 bottom-0 w-1 bg-primary-600 opacity-60 z-10' />
  );
};
